import {Command, Extension} from '@tiptap/core'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blockIndent: {
      indent: () => ReturnType
      outdent: () => ReturnType
    }
  }
}

const MAX_INDENT = 8
const INDENT_TYPES = ['paragraph', 'heading', 'blockquote']

/**
 * Block indentation rendered as an inline `margin-left` style so stored HTML
 * needs no editor CSS. Inside lists the indent buttons nest/lift list items
 * instead. Parses legacy `ql-indent-N` margins produced by the old Quill
 * editor (already converted to margin-left by normalizeLegacyRichText).
 */
export const BlockIndent = Extension.create({
  name: 'blockIndent',

  addGlobalAttributes() {
    return [
      {
        types: INDENT_TYPES,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const margin = parseFloat(element.style.marginLeft || '0')
              if (!margin) return 0
              // one indent step = 2rem = 32px
              const step = element.style.marginLeft.endsWith('rem') ? margin / 2 : margin / 32
              return Math.min(MAX_INDENT, Math.max(0, Math.round(step)))
            },
            renderHTML: (attributes) => {
              if (!attributes.indent) return {}
              return {style: `margin-left: ${attributes.indent * 2}rem`}
            },
          },
        },
      },
    ]
  },

  addCommands() {
    const changeIndent =
      (delta: 1 | -1): Command =>
      ({editor, chain, commands}) => {
        if (editor.isActive('listItem')) {
          return delta > 0 ? commands.sinkListItem('listItem') : commands.liftListItem('listItem')
        }
        let applied = false
        const chained = chain()
        INDENT_TYPES.forEach((type) => {
          if (!editor.isActive(type)) return
          const current = editor.getAttributes(type).indent ?? 0
          const next = Math.min(MAX_INDENT, Math.max(0, current + delta))
          if (next !== current) {
            chained.updateAttributes(type, {indent: next})
            applied = true
          }
        })
        return applied ? chained.run() : false
      }

    return {
      indent: () => changeIndent(1),
      outdent: () => changeIndent(-1),
    }
  },
})
